import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { BOTTOM_NAV, MAIS_IDS } from "../constants/nav";
import { TOUR_STEPS, TAB_AREA_IDS, getAreaSteps } from "../constants/tour";

/* Tutorial guiado obrigatório na 1ª vez (ver constants/tour.js pros passos).
   Fica só com "Próximo" — sem X pra fechar — até acabar; depois disso só
   reabre via botão manual em Configurações. O progresso concluído é
   gravado em users/{uid}.tutorialConcluido, então não repete em outro
   login/dispositivo depois de terminado uma vez. */
export function useTour({ uid, perfil, setPerfil, isMobile, setTab, setShowMais, resetOverlays, enabled }) {
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  // Ref, não state: essa flag só existe pra "já decidi, não checar de novo" —
  // se fosse state (e por consequência entrasse na dependência do efeito
  // abaixo), o próprio ato de marcá-la re-executa o efeito (autoChecked
  // mudou), o cleanup cancela o setTimeout que tinha acabado de agendar, e o
  // tutorial nunca abre sozinho. Ref não dispara re-render nem reexecução.
  const autoCheckedRef = useRef(false);
  // Também ref: precisa estar atualizada ANTES do próximo render (startArea
  // muda vários states de uma vez) e não deve disparar reexecução de efeito
  // por si só — mesmo raciocínio do autoCheckedRef acima.
  const isAreaTourRef = useRef(false);

  const [steps, setSteps] = useState(TOUR_STEPS);
  const tabAreaIds = useMemo(() => new Set(TAB_AREA_IDS), []);

  // Deixa visível o elemento que o passo aponta: troca de aba (desktop) ou
  // abre o hub "Mais" (mobile) quando a área mora lá; ícones de atalho
  // sempre visíveis (clientes/agenda/impressoras/config) não precisam de nada.
  const prepare = useCallback((step) => {
    resetOverlays();
    if (!step || step.kind === "bookend") return;
    if (step.kind === "sub" || step.kind.startsWith("interactive-")) { setTab(step.forceTab); return; }
    const id = step.id;
    if (isMobile) {
      if (BOTTOM_NAV.some(b => b.id === id)) setTab(id);
      else if (MAIS_IDS.has(id)) setShowMais(true);
    } else {
      if (tabAreaIds.has(id)) setTab(id);
    }
  }, [isMobile, tabAreaIds, setTab, setShowMais, resetOverlays]);

  const goTo = useCallback((i) => {
    const step = steps[i];
    if (!step) return;
    prepare(step);
    setIndex(i);
  }, [steps, prepare]);

  const start = useCallback(() => {
    isAreaTourRef.current = false;
    setSteps(TOUR_STEPS);
    setActive(true);
    prepare(TOUR_STEPS[0]);
    setIndex(0);
  }, [prepare]);

  // Tutorial de UMA área só (acionado sob demanda) — não é o
  // onboarding completo, então não mexe em "tutorialConcluido". Chama
  // prepare()/setIndex() direto em vez de via goTo() porque goTo() lê
  // "steps" do state, que só reflete o setSteps() abaixo no PRÓXIMO
  // render (fica evitado aqui pra não abrir no passo errado).
  const startArea = useCallback((tabId) => {
    const areaSteps = getAreaSteps(tabId);
    if (!areaSteps.length) return false;
    isAreaTourRef.current = true;
    setSteps(areaSteps);
    setActive(true);
    prepare(areaSteps[0]);
    setIndex(0);
    return true;
  }, [prepare]);

  const finish = useCallback(() => {
    setActive(false);
    resetOverlays();
    const eraAreaTour = isAreaTourRef.current;
    isAreaTourRef.current = false;
    setSteps(TOUR_STEPS);
    setIndex(0);
    if (eraAreaTour) return; // mini-tutorial de uma área — não conta como onboarding concluído
    if (uid) updateDoc(doc(db, "users", uid), { tutorialConcluido: true }).catch(() => {});
    setPerfil(p => p ? { ...p, tutorialConcluido: true } : p);
  }, [uid, setPerfil, resetOverlays]);

  // Fecha o mini-tutorial de área antes de terminar (botão ✕) — o
  // onboarding completo não tem esse botão de propósito (ver TourOverlay.jsx).
  const skip = useCallback(() => { finish(); }, [finish]);

  const next = useCallback(() => {
    if (index >= steps.length - 1) { finish(); return; }
    goTo(index + 1);
  }, [index, steps.length, goTo, finish]);

  const back = useCallback(() => { if (index > 0) goTo(index - 1); }, [index, goTo]);

  // Logout (ou troca de conta): reseta o tour pra não reaparecer "no meio"
  // numa sessão de outro usuário nem ficar preso achando que já checou.
  useEffect(() => {
    if (!uid) { setActive(false); autoCheckedRef.current = false; setIndex(0); }
  }, [uid]);

  // Auto-início: uma vez que o perfil carregou, se ainda não concluiu.
  // Depende só de valores primitivos (não do objeto `perfil` inteiro) — se
  // dependesse da referência do objeto, um setPerfil(...) disparado por
  // OUTRA coisa (ex.: escolher o plano/trial logo depois do cadastro,
  // poucos ms antes de terminar o delay abaixo) reexecutava este efeito,
  // o React cancelava o setTimeout no cleanup, e o tutorial nunca chegava
  // a abrir sozinho — mesmo com tudo "certo" internamente. Pela mesma razão,
  // "já verifiquei" é um ref (autoCheckedRef), não state — um state aqui
  // entraria nas deps e o próprio ato de marcá-lo reexecutaria o efeito.
  const perfilCarregado    = !!perfil;
  const tutorialConcluido  = !!perfil?.tutorialConcluido;
  useEffect(() => {
    if (!enabled || autoCheckedRef.current || !perfilCarregado || active) return;
    if (!tutorialConcluido) {
      const t = setTimeout(() => { autoCheckedRef.current = true; start(); }, 700);
      return () => clearTimeout(t);
    }
    autoCheckedRef.current = true;
  }, [enabled, perfilCarregado, tutorialConcluido, active, start]);

  return { active, step: steps[index], index, total: steps.length, next, back, start, startArea, skip, isAreaTour: isAreaTourRef.current };
}
